import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import productsApi from '../../../api/productsApi';
import { applyServerErrors, getErrorMessage } from '../../../utils/errorHandler';
import CategoryChecklist from './CategoryChecklist';
import ProductImagesManager from './ProductImagesManager';
import ProductSerialsManager from './ProductSerialsManager';
import useAuth from '../../../auth/useAuth';
import { ROLES } from '../../../config';

const schema = yup.object({
  // Maximo 30: es el codigo de producto (SellersItemIdentification) que SUNAT exige en cada
  // boleta/factura; uno mas largo genera la observacion 4269 al emitir ("codigo de producto
  // no cumple con el formato establecido").
  sku: yup.string().trim().required('El SKU es obligatorio').max(30, 'Máximo 30 caracteres (lo exige SUNAT)'),
  name: yup.string().trim().required('El nombre es obligatorio').max(255),
  description: yup.string().nullable(),
  category_ids: yup.array().of(yup.number()).min(1, 'Marca al menos una categoría'),
  brand_id: yup
    .number()
    .typeError('Selecciona una marca')
    .required('Selecciona una marca'),
  price_usd: yup
    .number()
    .typeError('Ingresa un precio válido')
    .min(0, 'Debe ser mayor o igual a 0')
    .required('El precio en dólares es obligatorio'),
  price_usd_distribuidor: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .typeError('Ingresa un precio válido')
    .min(0, 'Debe ser mayor o igual a 0')
    .nullable(),
  offer_type: yup
    .string()
    .transform((value) => (value === '' ? null : value))
    .oneOf(['oferta', 'remate', null])
    .nullable(),
  price_usd_regular: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .typeError('Ingresa un precio válido')
    .nullable()
    .when('price_usd', ([priceUsd], field) =>
      typeof priceUsd === 'number' ? field.moreThan(priceUsd, 'Debe ser mayor al precio de venta') : field
    ),
  cost: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .typeError('Ingresa un costo válido')
    .min(0, 'Debe ser mayor o igual a 0')
    .nullable(),
  stock_quantity: yup
    .number()
    .typeError('Ingresa un número válido')
    .integer('Debe ser un número entero')
    .min(0, 'Debe ser mayor o igual a 0')
    .required('El stock es obligatorio'),
  express_delivery: yup.boolean().default(true),
  is_active: yup.boolean().default(true),
});

const emptyValues = {
  sku: '',
  name: '',
  description: '',
  category_ids: [],
  brand_id: '',
  price_usd: '',
  price_usd_distribuidor: '',
  offer_type: '',
  price_usd_regular: '',
  cost: '',
  stock_quantity: 0,
  express_delivery: true,
  is_active: true,
};

// Formulario de alta/edicion de un producto. `product` nulo significa "crear nuevo".
export default function ProductFormDialog({
  open,
  onClose,
  onSaved,
  product,
  categories,
  brands,
  onImagesChanged,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const isAdmin = user?.role === ROLES.ADMINISTRADOR;
  const isEditing = Boolean(product);

  const [images, setImages] = useState([]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: emptyValues,
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    setImages(product?.images || []);
    reset(
      product
        ? {
            sku: product.sku,
            name: product.name,
            description: product.description || '',
            category_ids: product.category_ids || (product.category_id ? [product.category_id] : []),
            brand_id: product.brand_id,
            price_usd: product.price_usd,
            price_usd_distribuidor: product.price_usd_distribuidor ?? '',
            offer_type: product.offer_type ?? '',
            price_usd_regular: product.price_usd_regular ?? '',
            cost: product.cost ?? '',
            stock_quantity: product.stock_quantity,
            express_delivery: product.express_delivery ?? true,
            is_active: product.is_active,
          }
        : emptyValues
    );
  }, [open, product, reset]);

  // Las imagenes se suben/borran al toque (no forman parte del envio del formulario de
  // datos), asi que tras cada cambio se vuelve a pedir el producto para refrescar la
  // galeria de este dialogo y, aparte, se avisa al listado para que actualice su miniatura.
  const refreshImages = async () => {
    if (!product) {
      return;
    }
    try {
      const { data } = await productsApi.getProduct(product.id);
      setImages(data.data.images || []);
    } catch (error) {
      // Si falla, la galeria se queda con lo ultimo que se vio; no bloquea el resto del formulario.
    }
    onImagesChanged?.();
  };

  const onSubmit = async (data) => {
    try {
      if (isEditing) {
        await productsApi.updateProduct(product.id, data);
        enqueueSnackbar('Producto actualizado correctamente.', { variant: 'success' });
      } else {
        await productsApi.createProduct(data);
        enqueueSnackbar('Producto creado correctamente.', { variant: 'success' });
      }
      onSaved();
    } catch (error) {
      const handled = applyServerErrors(error, setError);
      if (!handled) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudo guardar el producto.'), { variant: 'error' });
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Editar producto' : 'Nuevo producto'}</DialogTitle>

      <DialogContent dividers>
        <Stack component="form" id="product-form" spacing={2.5} onSubmit={handleSubmit(onSubmit)} noValidate>
          {errors.root?.message && <Alert severity="error">{errors.root.message}</Alert>}

          <TextField
            label="SKU"
            fullWidth
            slotProps={{ htmlInput: { maxLength: 30 } }}
            error={Boolean(errors.sku)}
            helperText={errors.sku?.message || 'Máximo 30 caracteres: es el código de producto que SUNAT exige en el comprobante.'}
            {...register('sku')}
          />

          <TextField
            label="Nombre"
            fullWidth
            error={Boolean(errors.name)}
            helperText={errors.name?.message}
            {...register('name')}
          />

          <TextField
            label="Descripción"
            fullWidth
            multiline
            minRows={2}
            error={Boolean(errors.description)}
            helperText={errors.description?.message}
            {...register('description')}
          />

          <Controller
            name="brand_id"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Marca"
                fullWidth
                error={Boolean(errors.brand_id)}
                helperText={errors.brand_id?.message}
              >
                {brands.map((brand) => (
                  <MenuItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            name="category_ids"
            control={control}
            render={({ field }) => (
              <CategoryChecklist
                categories={categories}
                value={field.value}
                onChange={field.onChange}
                error={Boolean(errors.category_ids)}
                helperText={
                  errors.category_ids?.message ||
                  (isEditing && product.category
                    ? `Marca una o varias. Principal (se muestra en la tienda): ${product.category.name}.`
                    : 'Marca una o varias. La primera marcada será la principal (se muestra en la tienda).')
                }
              />
            )}
          />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: isAdmin ? 4 : 6 }}>
              <TextField
                label="Precio (USD)"
                type="number"
                fullWidth
                slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
                error={Boolean(errors.price_usd)}
                helperText={
                  errors.price_usd?.message || 'El precio en soles se calcula con el tipo de cambio configurado.'
                }
                {...register('price_usd')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: isAdmin ? 4 : 6 }}>
              <TextField
                label="Stock"
                type="number"
                fullWidth
                slotProps={{ htmlInput: { min: 0 } }}
                error={Boolean(errors.stock_quantity)}
                helperText={errors.stock_quantity?.message}
                {...register('stock_quantity')}
              />
            </Grid>
            {isAdmin && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Costo (US$)"
                  type="number"
                  fullWidth
                  slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
                  error={Boolean(errors.cost)}
                  helperText={errors.cost?.message || 'Precio de compra, visible solo para administradores.'}
                  {...register('cost')}
                />
              </Grid>
            )}
          </Grid>

          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                label="Precio distribuidor (USD)"
                type="number"
                fullWidth
                slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
                error={Boolean(errors.price_usd_distribuidor)}
                helperText={
                  errors.price_usd_distribuidor?.message ||
                  'Precio para el rol distribuidor. Vacío = usa el precio (USD) normal. El distribuidor lo ve y paga solo en soles, con el tipo de cambio distribuidor de Configuración.'
                }
                {...register('price_usd_distribuidor')}
              />
            </Grid>
          </Grid>

          {/* Pestaña "Ofertas" de la tienda: el precio que se cobra sigue siendo "Precio (USD)" */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="offer_type"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Oferta / remate"
                    fullWidth
                    error={Boolean(errors.offer_type)}
                    helperText={errors.offer_type?.message || 'Lo muestra en la pestaña "Ofertas" de la tienda.'}
                  >
                    <MenuItem value="">Ninguno</MenuItem>
                    <MenuItem value="oferta">Oferta</MenuItem>
                    <MenuItem value="remate">Remate / saldo</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Precio regular (USD)"
                type="number"
                fullWidth
                slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
                error={Boolean(errors.price_usd_regular)}
                helperText={
                  errors.price_usd_regular?.message || 'Opcional: precio anterior, se muestra tachado junto al precio.'
                }
                {...register('price_usd_regular')}
              />
            </Grid>
          </Grid>

          <Stack sx={{ flexWrap: 'wrap' }} direction="row" spacing={3}>
            <Controller
              name="is_active"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="Producto activo"
                />
              )}
            />
            <Controller
              name="express_delivery"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="Admite delivery express"
                />
              )}
            />
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle2">Series (unidades en stock)</Typography>
            {isEditing ? (
              <ProductSerialsManager productId={product.id} />
            ) : (
              <Alert severity="info">
                Guarda el producto primero para poder registrar sus números de serie.
              </Alert>
            )}
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle2">Imágenes</Typography>
            {isEditing ? (
              <ProductImagesManager productId={product.id} images={images} onChanged={refreshImages} />
            ) : (
              <Alert severity="info">Guarda el producto primero para poder agregarle imágenes.</Alert>
            )}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button type="submit" form="product-form" variant="contained" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ProductFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  product: PropTypes.object,
  categories: PropTypes.array.isRequired,
  brands: PropTypes.array.isRequired,
  onImagesChanged: PropTypes.func,
};

ProductFormDialog.defaultProps = {
  product: null,
  onImagesChanged: undefined,
};
